import { useMemo, useState } from "react";
import type {
  CoachingClip,
  CoachingNote,
  CoachingPlaylist,
  FeedbackBundle,
  MatchSummary,
} from "@/lib/types";
import { FeedbackPlayerModal } from "@/features/playback/FeedbackPlayerModal";
import { summarySections } from "./feedbackHelpers";

/**
 * `/feedback` Match Summaries tab (M7) — team-visible match
 * recaps. Ports legacy `renderFeedbackMatchSummaries`
 * (`js/coaching/feedback.js`).
 *
 * Each card: a "Match summary" kicker, the match label, a source
 * count, a preview of the first three recap sections, a
 * show/hide control for the full recap, and linked-evidence
 * buttons. The evidence buttons route into the M5
 * `<FeedbackPlayerModal>` — the linked note / clip / playlist is
 * resolved from the `/feedback` bundle by id (the bundle already
 * carries only the viewer-visible, scrubbed objects, so a
 * private linked item simply isn't in the map and its button
 * doesn't render).
 *
 * The full-recap toggle is a controlled-state button, NOT a
 * `<details>` — the project's no-native-chrome rule blocks
 * unstyled native disclosure (same call the M4 Composer made
 * for its structured-fields toggle).
 *
 * Privacy: the `match_summaries[]` + their `note_ids` /
 * `clip_ids` / `playlist_ids` are already viewer-filtered
 * server-side. This component never templates a private field.
 */

export type FeedbackSummariesTabProps = {
  bundle: FeedbackBundle | undefined;
};

export function FeedbackSummariesTab({ bundle }: FeedbackSummariesTabProps) {
  const summaries = bundle?.match_summaries ?? [];

  // The evidence buttons resolve a linked id to its full object —
  // index the bundle's notes / clips / playlists once.
  const notesById = useMemo(
    () => new Map((bundle?.notes ?? []).map((n) => [n.id, n])),
    [bundle?.notes],
  );
  const clipsById = useMemo(
    () => new Map((bundle?.clips ?? []).map((c) => [c.id, c])),
    [bundle?.clips],
  );
  const playlistsById = useMemo(
    () => new Map((bundle?.playlists ?? []).map((p) => [p.id, p])),
    [bundle?.playlists],
  );

  // The M5 modal target — one of note / clip / playlist, or null.
  const [openNote, setOpenNote] = useState<CoachingNote | null>(null);
  const [openClip, setOpenClip] = useState<CoachingClip | null>(null);
  const [openPlaylist, setOpenPlaylist] = useState<CoachingPlaylist | null>(
    null,
  );

  if (summaries.length === 0) {
    return (
      <p
        className="text-ink-muted text-sm px-3 py-6"
        data-testid="feedback-summaries-empty"
      >
        No team-visible match summaries yet.
      </p>
    );
  }

  return (
    <>
      <ul
        className="flex flex-col gap-2"
        data-testid="feedback-summaries-list"
      >
        {summaries.map((summary) => (
          <SummaryCard
            key={summary.id}
            summary={summary}
            notesById={notesById}
            clipsById={clipsById}
            playlistsById={playlistsById}
            onOpenNote={setOpenNote}
            onOpenClip={setOpenClip}
            onOpenPlaylist={setOpenPlaylist}
          />
        ))}
      </ul>

      {/* M5 focused player — opened from a linked-evidence button. */}
      {openNote ? (
        <FeedbackPlayerModal
          target={{ kind: "note", note: openNote }}
          match={{
            id: openNote.match_id ?? "",
            team_id: openNote.team_id,
          }}
          onClose={() => setOpenNote(null)}
        />
      ) : null}
      {openClip ? (
        <FeedbackPlayerModal
          target={{ kind: "clip", clip: openClip }}
          match={{ id: openClip.match_id, team_id: openClip.team_id }}
          onClose={() => setOpenClip(null)}
        />
      ) : null}
      {openPlaylist ? (
        <FeedbackPlayerModal
          target={{ kind: "playlist", playlist: openPlaylist }}
          match={{
            id: openPlaylist.items?.[0]?.match_id ?? "",
            team_id:
              openPlaylist.items?.[0]?.team_id ?? openPlaylist.team_id,
          }}
          onClose={() => setOpenPlaylist(null)}
        />
      ) : null}
    </>
  );
}

function SummaryCard({
  summary,
  notesById,
  clipsById,
  playlistsById,
  onOpenNote,
  onOpenClip,
  onOpenPlaylist,
}: {
  summary: MatchSummary;
  notesById: Map<number, CoachingNote>;
  clipsById: Map<number, CoachingClip>;
  playlistsById: Map<number, CoachingPlaylist>;
  onOpenNote: (n: CoachingNote) => void;
  onOpenClip: (c: CoachingClip) => void;
  onOpenPlaylist: (p: CoachingPlaylist) => void;
}) {
  // Controlled-state full-recap toggle — NOT a native <details>.
  const [showFull, setShowFull] = useState(false);

  const sections = summarySections(summary);

  // Resolve linked evidence — a private linked item simply isn't
  // in the bundle map, so its button is dropped.
  const linkedNotes = (summary.note_ids ?? [])
    .map((id) => notesById.get(id))
    .filter((n): n is CoachingNote => !!n);
  const linkedClips = (summary.clip_ids ?? [])
    .map((id) => clipsById.get(id))
    .filter((c): c is CoachingClip => !!c);
  const linkedPlaylists = (summary.playlist_ids ?? [])
    .map((id) => playlistsById.get(id))
    .filter((p): p is CoachingPlaylist => !!p);
  const sourceCount =
    linkedNotes.length + linkedClips.length + linkedPlaylists.length;

  const visibleSections = showFull ? sections : sections.slice(0, 3);
  const hasMore = sections.length > 3;

  return (
    <li
      data-testid="feedback-summary-card"
      data-summary-id={summary.id}
      className="rounded-lg border border-rule bg-bg-card px-3 py-2"
    >
      <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        Match summary
      </span>
      <h3 className="font-display text-ink truncate mt-0.5">
        {summary.match_id}
      </h3>
      <p className="text-ink-muted text-xs font-mono mt-0.5">
        {sourceCount > 0
          ? `${sourceCount} source${sourceCount === 1 ? "" : "s"} linked`
          : "Team recap"}
      </p>

      <div className="mt-2 flex flex-col gap-2">
        {visibleSections.map(([label, value]) => (
          <section key={label}>
            <h4 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              {label}
            </h4>
            <p className="text-ink text-sm mt-0.5">{value}</p>
          </section>
        ))}
      </div>

      {hasMore ? (
        <button
          type="button"
          onClick={() => setShowFull((v) => !v)}
          aria-expanded={showFull}
          data-testid="feedback-summary-toggle-full"
          className="mt-2 text-ink-muted hover:text-ink text-xs font-display uppercase tracking-wider transition-colors"
        >
          {showFull ? "Show less" : "Full match summary"}
        </button>
      ) : null}

      {/* Linked evidence — route into the M5 modal. */}
      {sourceCount > 0 ? (
        <div
          className="flex flex-wrap gap-1.5 mt-2"
          data-testid="feedback-summary-sources"
          aria-label="Linked evidence"
        >
          {linkedNotes.map((n) => (
            <EvidenceButton
              key={`note-${n.id}`}
              label={`Note: ${n.title?.trim() || "Moment"}`}
              onClick={() => onOpenNote(n)}
            />
          ))}
          {linkedClips.map((c) => (
            <EvidenceButton
              key={`clip-${c.id}`}
              label={`Clip: ${c.title?.trim() || "Clip"}`}
              onClick={() => onOpenClip(c)}
            />
          ))}
          {linkedPlaylists.map((p) => (
            <EvidenceButton
              key={`playlist-${p.id}`}
              label={`Playlist: ${p.title?.trim() || "Session"}`}
              onClick={() => onOpenPlaylist(p)}
            />
          ))}
        </div>
      ) : null}
    </li>
  );
}

function EvidenceButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="feedback-summary-source-button"
      className="border border-rule text-ink-muted hover:text-ink hover:bg-surface rounded px-2.5 py-1 text-xs font-display transition-colors"
    >
      {label}
    </button>
  );
}
